<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { ACCESS_LEVELS, ACCESS_SCORE_BOUNDS, accessLevelFromScore, isScoreInBounds, scoreForLevel, type AccessLevel } from './access_score'

const props = defineProps<{ modelValue: number | null }>()

const emit = defineEmits<{ 'update:modelValue': [score: number | null] }>()

const level = ref<AccessLevel | ''>(accessLevelFromScore(props.modelValue))
// Only an outside change (a reload) moves the level; a score typed here keeps the chosen
// level so an out-of-band entry shows as invalid, as it did in Vue 2.
let ownScore: number | null | undefined
watch(() => props.modelValue, score => {
  const isOwnScore = score === ownScore
  ownScore = undefined
  if (!isOwnScore) level.value = accessLevelFromScore(score)
})
const isEditingManually = ref(false)
const scoreInput = ref<HTMLInputElement | null>(null)
const editToggle = ref<HTMLButtonElement | null>(null)

const bounds = computed(() => level.value ? ACCESS_SCORE_BOUNDS[level.value] : { min: 0, max: 0 })
const isValid = computed(() => isScoreInBounds(level.value, props.modelValue))

function emitScore(score: number | null) {
  ownScore = score
  emit('update:modelValue', score)
}

function changeLevel(event: Event) {
  level.value = (event.target as HTMLSelectElement).value as AccessLevel | ''
  if (level.value) emitScore(scoreForLevel(level.value, props.modelValue))
}

function changeScore(event: Event) {
  emitScore(parseFloat((event.target as HTMLInputElement).value) || null)
}

// Clicking Edit to switch it off blurs the score first; letting that blur toggle too would
// switch it straight back on, as it did in Vue 2.
function stopEditingUnlessToggling(event: FocusEvent) {
  if (event.relatedTarget !== editToggle.value) isEditingManually.value = false
}

async function toggleManualEdit() {
  isEditingManually.value = !isEditingManually.value
  if (!isEditingManually.value) return
  await nextTick()
  scoreInput.value?.focus()
}
</script>

<template>
  <div class="mb-4">
    <label for="access" class="block mb-1 font-bold" :class="{ 'text-red-300': !isValid }">Access</label>
    <select
      id="access"
      :value="level"
      class="w-full h-10 px-3 text-md text-gray-900 bg-white border rounded-xs focus:outline-primary-400"
      :class="isValid ? 'border-gray-600' : 'border-red-400'"
      @change="changeLevel"
    >
      <option value="">(select access)</option>
      <option v-for="accessLevel in ACCESS_LEVELS" :key="accessLevel" :value="accessLevel">{{ accessLevel }}</option>
    </select>
    <label for="score" class="sr-only">Score</label>
    <div class="flex mt-2">
      <input
        id="score"
        ref="scoreInput"
        type="number"
        step="0.1"
        :value="modelValue"
        :min="bounds.min"
        :max="bounds.max"
        :disabled="!level || !isEditingManually"
        :aria-invalid="!isValid"
        :aria-describedby="level ? 'score-range' : undefined"
        class="grow h-8 px-3 text-sm text-gray-900 bg-white border rounded-l-xs disabled:bg-gray-300 disabled:text-gray-700 focus:outline-primary-400"
        :class="isValid ? 'border-gray-600' : 'border-red-400'"
        @input="changeScore"
        @blur="stopEditingUnlessToggling"
      >
      <button
        ref="editToggle"
        type="button"
        class="inline-flex items-center gap-1 h-8 px-3 text-sm text-gray-900 border border-l-0 border-gray-600 rounded-r-xs hover:bg-gray-300 focus-visible:outline-primary-400"
        :class="{ 'bg-gray-300': isEditingManually }"
        :aria-pressed="isEditingManually"
        @click="toggleManualEdit"
      >
        <span class="material-icons-round text-text-md" aria-hidden="true">edit</span> Edit
      </button>
    </div>
    <small v-if="level" id="score-range" class="block mt-1 text-gray-700">Between {{ bounds.min }} and {{ bounds.max }}</small>
  </div>
</template>
